import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, BRAND } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { PRODUCT } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Minus, Plus, ShieldCheck, Truck, Sparkles, BookOpen, Wine } from "lucide-react";
import { toast } from "sonner";
import { DocPreviewModal } from "@/components/DocPreviewModal";

export default function ProductPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const key = slug || "preshhgo-starter-kit";
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { format } = useCurrency();
  const [doc, setDoc] = useState(null);
  const [active, setActive] = useState(0);
  const [related, setRelated] = useState([]);
  const { add, salesSuspended } = useCart();

  useEffect(() => {
    api.get(`/products/${key}`).then((r) => { setProduct(r.data); setActive(0); });
    api.get("/products", { params: { category: "accessory" } }).then((r) => setRelated(r.data.slice(0, 3)));
  }, [key]);

  if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-white/60">{t("product.loading")}</div>;

  const catLabel = t(`product.category_${product.category}`, { defaultValue: product.category.toUpperCase() });
  const doAdd = () => { add(product, qty); toast.success(`${product.name} ${t("product.added_to_cart")}`); };

  return (
    <div className="text-white">
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid md:grid-cols-2 gap-10">
        <div>
          <div className="aspect-square rounded-3xl overflow-hidden bg-[#111] border border-white/10 mb-4">
            <img src={product.images[active]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            {product.images.map((img, i) => (
              <button key={i} onClick={() => setActive(i)}
                className={`aspect-square rounded-xl overflow-hidden border ${i === active ? "border-[#00B5D8]" : "border-white/10"} transition-colors`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="uppercase text-xs tracking-[0.3em] text-[#00B5D8] mb-3">{catLabel}</p>
          <h1 className="font-display text-4xl md:text-5xl">{product.name}</h1>
          {product.tagline && <p className="mt-3 text-white/70 text-lg">{product.tagline}</p>}
          <p className="mt-6 text-4xl font-display" data-testid="product-price">{format(product.price)} <span className="text-white/50 text-base align-middle">{t("vat_suffix")}</span></p>

          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <div className="inline-flex items-center gap-1 bg-white/5 rounded-full border border-white/10">
              <button data-testid={PRODUCT.quantityDecrement} onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3"><Minus size={16} /></button>
              <span className="min-w-8 text-center">{qty}</span>
              <button data-testid={PRODUCT.quantityIncrement} onClick={() => setQty((q) => q + 1)} className="p-3"><Plus size={16} /></button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {salesSuspended ? (
              <div data-testid="product-sales-paused" className="w-full rounded-xl bg-[#00B5D8]/10 border border-[#00B5D8]/30 p-4">
                <p className="font-semibold text-white">{t("sales_paused_title")}</p>
                <p className="mt-1 text-sm text-white/70">{t("sales_paused_body")}</p>
              </div>
            ) : (
              <>
                <Button data-testid={PRODUCT.buyNow} onClick={() => { doAdd(); }} className="bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold rounded-full px-8 py-6">{t("product.buy_now")}</Button>
                <Button data-testid={PRODUCT.addToCart} onClick={doAdd} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-6">{t("product.add_to_cart")}</Button>
              </>
            )}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-2 text-xs text-white/60">
            <div className="flex items-center gap-2"><Truck size={14} /> {t("product.fast_shipping")}</div>
            <div className="flex items-center gap-2"><ShieldCheck size={14} /> {t("product.secure_checkout")}</div>
          </div>

          <div className="mt-8 bg-[#111] border border-white/10 rounded-2xl p-6">
            <p className="text-white/80 leading-relaxed">{product.description}</p>
            {product.features?.length > 0 && (
              <ul className="mt-4 space-y-2 text-sm text-white/70">
                {product.features.map((f) => <li key={f} className="flex gap-2"><Sparkles size={14} className="text-[#00B5D8] mt-1" /> {f}</li>)}
              </ul>
            )}
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3">
              <button
                type="button"
                data-testid="product-manual-link"
                onClick={() => {
                  setDoc({ url: BRAND.manualUrl, title: t("product.read_manual") });
                  setTimeout(() => window.dispatchEvent(new Event("preshh:mat-hint")), 1500);
                }}
                className="inline-flex items-center gap-2 text-sm text-[#00B5D8] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] rounded">
                <BookOpen size={15} /> {t("product.read_manual")}
              </button>
              <button
                type="button"
                data-testid="product-recipes-link"
                onClick={() => setDoc({ url: BRAND.recipesUrl, title: t("product.read_recipes") })}
                className="inline-flex items-center gap-2 text-sm text-[#00B5D8] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] rounded">
                <Wine size={15} /> {t("product.read_recipes")}
              </button>
            </div>
          </div>
        </div>
      </section>

      <DocPreviewModal
        open={!!doc}
        onOpenChange={(o) => !o && setDoc(null)}
        url={doc?.url}
        title={doc?.title}
        downloadLabel={t("support.manual_cta")}
        openLabel={t("support.open_new_tab")}
      />

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <h2 className="font-display text-3xl md:text-4xl mb-8">{t("product.complete_setup")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {related.map((r) => (
              <div key={r.id} className="bg-[#111] border border-white/10 rounded-2xl p-4 flex gap-4">
                <img src={r.images[0]} alt={r.name} className="w-20 h-20 rounded-lg object-cover" />
                <div className="flex-1">
                  <p className="font-semibold text-sm">{r.name}</p>
                  <p className="text-[#00B5D8] text-sm mt-1">{format(r.price)} <span className="text-white/50 text-xs">{t("vat_suffix")}</span></p>
                  {!salesSuspended && (
                  <button onClick={() => { add(r, 1); toast.success(`${r.name} ${t("product.added_to_cart")}`); }}
                    className="mt-2 text-xs bg-white/10 hover:bg-white/20 rounded-full px-3 py-1 transition-colors">{t("product.add")}</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
